// @flow

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as R from 'ramda';
import moment from 'moment';
import SPBackend from '../../services/SPBackend';
import { log, numberWithCommas } from '../../utils/jsUtils';
import { getDisplayPrice } from '../../utils/pricingUtils';

import CreditCardForm from '../SharedComponents/CreditCardForm';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MemberDrawer from '../MemberDrawer/index';
import BillingHeader from './BillingHeader';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/BillingContent.Style';
import commonListTableStyles from '../Styles/CommonListTable.Style';
import MemberStructuralStyles from '../Styles/CommonMemberStructuralStyles.Style';
import ConfirmTopupPurchaseDialog from './ConfirmTopupPurchaseDialog';
import UpdatePaymentMethodDialog from './UpdatePaymentMethodDialog';
import { selectUser, selectAccountUsage } from '../../reduxStore/modules/user/selectors';
import { selectBillingState } from '../../reduxStore/modules/billing/selectors';
import CircularProgress from '@mui/material/CircularProgress';
import { setBilling } from '../../reduxStore/modules/billing/actions';
import { fetchPlanInfo } from '../../reduxStore/modules/billing/actions';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { getBillingStateMessage, hasNoSubscription } from '../../utils/billingValidationUtils';
import DisableAutoRenewDialog from './DisableAutoRenewDialog';

import type { Plan } from '../../services/SPBackend';

type Props = {};

const getStatusChipProps = (status: string): { label: string, color: string } => {
  switch (status) {
    case 'active':
      return { label: 'Active', color: 'success' };
    case 'trialing':
      return { label: 'Trial', color: 'success' };
    case 'past_due':
      return { label: 'Past Due', color: 'error' };
    case 'incomplete':
      return { label: 'Incomplete', color: 'warning' };
    case 'unpaid':
      return { label: 'Unpaid', color: 'error' };
    case 'paused':
      return { label: 'Paused', color: 'warning' };
    case 'canceled':
      return { label: 'Canceled', color: 'default' };
    default:
      return { label: status, color: 'default' };
  }
};

// eslint-disable-next-line react/no-multi-comp
const BillingContent = (props: Props) => {
  const theme = useTheme();
  const classes = createClasses({
    ...styles,
    ...commonListTableStyles,
    ...MemberStructuralStyles
  }, theme);
  
  const dispatch = useDispatch();

  // drawer state & controls
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [topupPlans, setTopupPlans] = useState([]);
  const [openConfirmTopupDialog, setOpenConfirmTopupDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planInfo, setPlanInfo] = useState({
    nickname: '',
    active: true,
    amount: 0,
    currency: 'usd',
    interval: 'month',
    interval_count: 0,
    product: ''
  });
  const [invoices, setInvoices] = useState([]);
  const [isConfirmTopupPurchaseLoading, setIsConfirmTopupPurchaseLoading] = useState(false);
  const [openUpdatePaymentMethodDialog, setOpenUpdatePaymentMethodDialog] = useState(false);
  const [openDisableAutoRenewDialog, setOpenDisableAutoRenewDialog] = useState(false);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [productInfo, setProductInfo] = useState({
    name: ''
  });
  const [subscription, setSubscription] = useState(null);
  const [discount, setDiscount] = useState(null);

  const handleConfirmTopupPurchase = () => {
    setIsConfirmTopupPurchaseLoading(true);
    log('[handleConfirmTopupPurchase]....');
    if (typeof selectedPlan !== 'undefined' && selectedPlan !== null) {
      SPBackend.purchaseTopupPlan(user, selectedPlan._id)
      .then(response => {
        log('[purchaseTopupPlan] response: ', response);
        const { data } = response;
        setOpenConfirmTopupDialog(false);
        setIsConfirmTopupPurchaseLoading(false);
        dispatch(setSnackMessage({
          message: data.message,
          level: data.success,
          duration: 5000
        }));
      })
      .catch(err => {
        setLoading(false);
        setIsConfirmTopupPurchaseLoading(false);
        dispatch(setSnackMessage({
          message: err.message,
          level: 'error',
          duration: 5000
        }));
      });
    } else {
      dispatch(setSnackMessage({
        message: 'Select a recharge to buy',
        level: 'error',
        duration: 5000
      }));
    }
  };

  const handleSelectPlanToPurchase = (plan) => {
    setSelectedPlan(plan);
    setOpenConfirmTopupDialog(true);
  };

  const user = useSelector(selectUser);
  const billing = useSelector(selectBillingState);
  const accountUsage = useSelector(selectAccountUsage);
  log('[render] accountUsage: ', accountUsage);

  const getPaymentMethods = () => {
    SPBackend.getPaymentMethods(user)
      .then(response => {
        log('[getPaymentMethods] response: ', response);
        const {
          paymentMethods,
          defaultPaymentMethod
        } = response.data;
        setPaymentMethods(paymentMethods);
        setDefaultPaymentMethod(defaultPaymentMethod);
      })
      .catch(e => {
        log('[getPaymentMethods] error: ', e);
      });
  };

  useEffect(() => {
    // Guard: Don't fetch plan info if subscriptionId is not yet available (prevents /plan/null API call)
    if (!billing.subscriptionId) {
      return;
    }

    setLoading(true);
    if (user) {
      log('billing, user: ', billing, user);

      // PERFORMANCE OPTIMIZATION: Load all billing data in parallel instead of sequential
      Promise.all([
        SPBackend.getPlanInfo(billing.subscriptionId, user),
        SPBackend.getMembersTopupPlans(user),
        SPBackend.getAccountInvoices(user),
        SPBackend.getPaymentMethods(user)
      ])
      .then(([planInfoResponse, topupPlansResponse, invoicesResponse, paymentMethodsResponse]) => {
        log('[Billing] All API calls completed in parallel');

        // Handle plan info response
        log('[getPlanInfo] response: ', planInfoResponse.data);
        const { plan, product, discount, subscription, account } = planInfoResponse.data;
        setDiscount(discount);
        setPlanInfo(plan);
        setProductInfo(product);
        setSubscription(subscription);
        dispatch(setBilling({
          customerId: account.Billing ? account.Billing.CustomerId : null,
          subscriptionId: subscription.id,
          subscriptionStatus: subscription.status
        }));

        // Handle topup plans response
        log('[getMembersTopupPlans] response: ', topupPlansResponse.data);
        const { plans } = topupPlansResponse.data;
        setTopupPlans(plans);

        // Handle invoices response
        log('[getAccountInvoices] response: ', invoicesResponse);
        setInvoices(invoicesResponse.data);

        // Handle payment methods response
        // FIX: Properly destructure API response to extract paymentMethods array and defaultPaymentMethod object
        // Previously: setPaymentMethods(paymentMethodsResponse.data) - incorrectly set entire response object
        // This caused "paymentMethods.map is not a function" error at line 395
        log('[getPaymentMethods] response: ', paymentMethodsResponse);
        const { paymentMethods, defaultPaymentMethod } = paymentMethodsResponse.data;
        setPaymentMethods(paymentMethods || []); // Defensive: fallback to empty array
        setDefaultPaymentMethod(defaultPaymentMethod || null); // Defensive: fallback to null

        setLoading(false);
      })
      .catch(err => {
        log('[Billing] Error in parallel API calls: ', err);
        setLoading(false);
        // Ensure safe state even on error
        setPaymentMethods([]);
        setDefaultPaymentMethod(null);
        dispatch(setSnackMessage({
          message: err.message || 'Failed to load billing information',
          level: 'error',
          duration: 5000
        }));
      });
    }
  }, [billing.subscriptionId, billing.refreshIndex]);

  const onPaymentMethodSelected = (pm) => {
    // support only 1 pm at a time, so this will be default
    setPaymentMethods([pm]);
    setDefaultPaymentMethod(pm);
  };

  const handleDisableAutoRenew = () => {
    setAutoRenewLoading(true);
    SPBackend.disableAutoRenew(user)
      .then(() => {
        setOpenDisableAutoRenewDialog(false);
        dispatch(setSnackMessage({
          message: 'Auto-renew has been disabled',
          level: 'success',
          duration: 5000
        }));
        dispatch(fetchPlanInfo(billing.subscriptionId, user));
      })
      .catch(err => {
        dispatch(setSnackMessage({
          message: err.message || 'Failed to disable auto-renew',
          level: 'error',
          duration: 5000
        }));
      })
      .finally(() => {
        setAutoRenewLoading(false);
      });
  };

  const handleEnableAutoRenew = () => {
    setAutoRenewLoading(true);
    SPBackend.enableAutoRenew(user)
      .then(() => {
        dispatch(setSnackMessage({
          message: 'Auto-renew has been re-enabled',
          level: 'success',
          duration: 5000
        }));
        dispatch(fetchPlanInfo(billing.subscriptionId, user));
      })
      .catch(err => {
        dispatch(setSnackMessage({
          message: err.message || 'Failed to enable auto-renew',
          level: 'error',
          duration: 5000
        }));
      })
      .finally(() => {
        setAutoRenewLoading(false);
      });
  };

  return (
    <Grid
      sx={classes.billingContentContainer}
    >
      {
        loading
        ? <Typography>{'Loading your account...'}</Typography>
        : <Grid>
          <Card sx={classes.cardCtr}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h3">{'Membership plan'}</Typography>
                {billing && billing.subscriptionStatus && (
                  <Chip
                    label={getStatusChipProps(billing.subscriptionStatus).label}
                    color={getStatusChipProps(billing.subscriptionStatus).color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
            {
              accountUsage && accountUsage.CurrentPeriod
              ? billing.subscriptionStatus === 'canceled'
                ? <Typography sx={{ mt: 2 }}>No active subscription</Typography>
                : <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Plan</Typography>
                      <Typography fontWeight={600}>{
                        planInfo.id === process.env.REACT_APP_ACCOUNT_DEFAULT_SIGNUP_PLAN
                        ? 'Trial'
                        : `$${numberWithCommas(getDisplayPrice(planInfo, subscription?.quantity || 1) || 0)}/${planInfo.recurring?.interval || planInfo.interval || 'month'}`
                      }</Typography>
                    </Box>

                    {
                      subscription && accountUsage && accountUsage.CurrentPeriod
                      ? <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color="text.secondary">Billing period</Typography>
                          <Typography fontWeight={600}>{`${moment.unix(subscription.items?.data?.[0]?.current_period_start || subscription.current_period_start).format('MMM D, YYYY')} → ${moment.unix(subscription.items?.data?.[0]?.current_period_end || subscription.current_period_end).format('MMM D, YYYY')}`}</Typography>
                        </Box>
                      : null
                    }
                    {
                      discount && discount.object === 'discount'
                      ? <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color="text.secondary">Discount code</Typography>
                          <Typography fontWeight={600}>{discount.coupon.id}</Typography>
                        </Box>
                      : null
                    }
                  </Box>
                  {
                    subscription && planInfo.trial_period_days > 0 && subscription.trial_end !== null
                    ? <Grid item
                            xs={12}
                      >
                        <p>
                          {
                            moment.unix(subscription.trial_end).isAfter(moment())
                            ? <span>
                                {'Your free trial lasts until '}
                                <b>{`${moment.unix(subscription.trial_end).format('MMMM D, YYYY [at] hA')}.`}</b>
                                <br />
                              </span>
                            : null
                          }
                        </p>
                        <p>{'For any changes to your account, please text us: +‭1 (917) 451-5515.'}</p>
                      </Grid>
                    : null
                  }
                  {
                    subscription && (billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing')
                    ? <>
                        <Divider sx={{ my: 2 }} />
                        {subscription.cancel_at_period_end
                          ? <Box>
                              <Typography variant="body2" color="text.secondary">
                                Auto-renew is disabled. Your plan ends on <b>{(subscription.items?.data?.[0]?.current_period_end || subscription.current_period_end) ? moment.unix(subscription.items?.data?.[0]?.current_period_end || subscription.current_period_end).format('MMMM D, YYYY') : 'your billing period end'}</b>.
                              </Typography>
                              <Button
                                variant="text"
                                size="small"
                                onClick={handleEnableAutoRenew}
                                disabled={autoRenewLoading}
                                sx={{ mt: 0.5, p: 0, textTransform: 'none' }}
                              >
                                {autoRenewLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                Re-enable auto-renew
                              </Button>
                            </Box>
                          : <Box>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => setOpenDisableAutoRenewDialog(true)}
                                sx={{ p: 0, textTransform: 'none' }}
                              >
                                Disable auto-renew
                              </Button>
                            </Box>
                        }
                      </>
                    : null
                  }
                </>
              : <Typography>Your account is still being setup, refresh this page in a few minutes.</Typography>
            }
            </CardContent>
          </Card>
          {
            subscription && accountUsage && accountUsage.CurrentPeriod
            ? <div>
                <Card sx={classes.cardCtr}>
                  <CardContent>
                    <Typography variant="h3">Credits available: {Number.isNaN(Number(accountUsage.TotalCreditsBalance)) ? 0 : Number(accountUsage.TotalCreditsBalance).toLocaleString()}</Typography>
                  <TableContainer component={Paper}>
                    <Table aria-label="Credits Breakdown"
                      sx={classes.usageTable}
                    >
                      <TableHead>
                        <TableRow sx={classes.textBold}>
                          <TableCell>Source of Credits</TableCell>
                          <TableCell align="right">Quota</TableCell>
                          <TableCell align="right">Used</TableCell>
                          <TableCell align="right">Available</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow key={'credit-m'}>
                          <TableCell component="th"
                                     scope="row"
                          >
                            Membership plan
                          </TableCell>
                          <TableCell align="right">{Number.isNaN(Number(accountUsage.CurrentPeriod.Limit)) ? 0 : Number(accountUsage.CurrentPeriod.Limit).toLocaleString()}</TableCell>
                          <TableCell align="right">{Number.isNaN(Number(accountUsage.CurrentPeriod.CreditsUsed)) ? 0 : Number(accountUsage.CurrentPeriod.CreditsUsed).toLocaleString()}</TableCell>
                          <TableCell align="right">{Number.isNaN(Number(accountUsage.CurrentPeriod.Limit - accountUsage.CurrentPeriod.CreditsUsed)) ? 0 : Number(accountUsage.CurrentPeriod.Limit - accountUsage.CurrentPeriod.CreditsUsed).toLocaleString()}</TableCell>
                        </TableRow>
                        {accountUsage.Topups && accountUsage.Topups.map((t, tidx) => (
                          <TableRow key={`topup-${tidx}`}>
                            <TableCell component="th"
scope="row"
                            >
                              {`$${Number(t.AmountPaid / 100).toLocaleString()} on ${moment(t.CreatedDt).format('MM/DD/YY')} - Order #${t.Id}`}
                            </TableCell>
                            <TableCell align="right">{Number.isNaN(Number(t.Limit)) ? 0 : Number(t.Limit).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number.isNaN(Number(t.CreditsUsed)) ? 0 : Number(t.CreditsUsed).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number.isNaN(Number(t.Limit - t.CreditsUsed)) ? 0 : Number(t.Limit - t.CreditsUsed).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {/* <p>Period-to-date total usage: <b>{Number(accountUsage.CurrentPeriod.Usage)} message{accountUsage.CurrentPeriod.Usage > 1 ? 's' : ''}</b></p>
                  <p>Current period usage limit: <b>{accountUsage.CurrentPeriod.Limit} messages</b></p> */}
                    <p />
                  </CardContent>
                </Card>
              </div>
            : null
          }
          <Card sx={classes.cardCtr}>
            <CardContent>
              <Typography variant="h3">Recharges</Typography>
              <Typography variant='body1'>Add credits to your account on top of your subscription.
              <br />Credits are valid for 12 months.
              </Typography>
            <TableContainer sx={classes.topupTable}
                            component={Paper}
                            elevation={2}
            >
            <Table
              aria-label="Recharges"
              
            >
              <TableHead>
                <TableRow sx={classes.tableHeadRowCtr}>
                  <TableCell sx={classes.col1}>{'Package'}</TableCell>
                  <TableCell sx={classes.col2}>{'Credits'}</TableCell>
                  <TableCell sx={classes.col3}>{'Price per Credit'}</TableCell>
                  <TableCell sx={classes.col4}>{'Price'}</TableCell>
                  <TableCell sx={classes.col5} />
                </TableRow>
              </TableHead>
              <TableBody>
              {
                topupPlans && topupPlans.map((topup: Plan, topupIdx) => (
                  <TableRow
                    key={`topup-${topupIdx}`}
                  >
                    <TableCell>
                      {topupIdx + 1}  
                    </TableCell>
                    <TableCell>
                      {numberWithCommas(topup.UsageLimit)}
                    </TableCell>
                    <TableCell>
                      ${numberWithCommas(Math.round((topup.Price * 1000) / topup.UsageLimit) / 1000)}
                    </TableCell>
                    <TableCell sx={classes.priceCell}>
                      ${topup.Price}
                    </TableCell>
                    <TableCell sx={classes.col5}>
                      <Button
                        color='primary'
                        onClick={() => handleSelectPlanToPurchase(topup)}
                        variant='contained'
                      >{'Buy'}</Button>
                    </TableCell>
                  </TableRow>
                ))
              }
              </TableBody>
            </Table>
            </TableContainer>
            </CardContent>
          </Card>
          <Card sx={classes.cardCtr}>
            <CardContent>
              <Typography variant="h3">{'Your payment details'}</Typography>
              {
                // DEFENSIVE: Use Array.isArray() to prevent "paymentMethods.map is not a function" error
                // This catches cases where paymentMethods might be an object instead of an array
                Array.isArray(paymentMethods) && paymentMethods.length > 0
                ? paymentMethods.map((pm, i) => {
                  return (
                    <Typography key={`pm-${i}`} variant="body2" component="span" sx={{ display: 'block' }}>
                      <Box component="span" sx={classes.capitalize}>{pm.card.brand}</Box> ({pm.card.last4}) - Expires {pm.card.exp_month}/{pm.card.exp_year}&nbsp;&nbsp;
                      {defaultPaymentMethod && pm.id === defaultPaymentMethod.id ? <Chip sx={classes.defaultChip}
label='default'
                                                                                   /> : null}
                    </Typography>
                  );
                })
                : null
              }
              {
                !R.isEmpty(paymentMethods)
                ? <Box
                  sx={classes.link}
                  onClick={(e) => {
                    setOpenUpdatePaymentMethodDialog(true);
                  }}
                  ><br />{'Update payment method'}</Box>
                : null
              }
              {
                R.isEmpty(paymentMethods)
                ? <Grid>
                    <CreditCardForm 
                      onPaymentMethodSelected={onPaymentMethodSelected}
                    />
                  </Grid>
                : null
              }
            </CardContent>
          </Card>
          <Card sx={classes.cardCtr}>
            <CardContent>
              <Typography variant="h3">{'Invoices'}</Typography>
            {
              invoices
              ? invoices.map((invoice, i) => (
                <Grid container
                      key={`invoice-${i}`}
                >
                  <Grid item
                        xs={7}
                  >
                    <Typography variant="body2">
                      {moment.unix(invoice.created).format('YYYY-MM-DD [-] MMMM')}
                    </Typography>
                  </Grid>
                  <Grid item
                        xs={5}
                  ><a style={classes.downloadLink}
                                        href={invoice.pdf}
                   ><DownloadIcon /></a></Grid>
                </Grid>
              ))
              : null
            }
            </CardContent>
          </Card>
        </Grid>
      }
      
      <ConfirmTopupPurchaseDialog
        defaultPaymentMethod={defaultPaymentMethod}
        isActionLoading={isConfirmTopupPurchaseLoading}
        open={openConfirmTopupDialog}
        selectedPlan={selectedPlan}
        setOpen={setOpenConfirmTopupDialog}
        setSelectedPlan={setSelectedPlan}
        triggerAction={handleConfirmTopupPurchase}
      />
      <UpdatePaymentMethodDialog
        defaultPaymentMethod={defaultPaymentMethod}
        isActionLoading={isConfirmTopupPurchaseLoading}
        onPaymentMethodSelected={onPaymentMethodSelected}
        open={openUpdatePaymentMethodDialog}
        setOpen={setOpenUpdatePaymentMethodDialog}
        triggerAction={onPaymentMethodSelected}
      />
      <DisableAutoRenewDialog
        open={openDisableAutoRenewDialog}
        onClose={() => setOpenDisableAutoRenewDialog(false)}
        onConfirm={handleDisableAutoRenew}
        periodEndDate={subscription && (subscription.items?.data?.[0]?.current_period_end || subscription.current_period_end) ? moment.unix(subscription.items?.data?.[0]?.current_period_end || subscription.current_period_end).format('MMMM D, YYYY') : ''}
        loading={autoRenewLoading}
      />
    </Grid>
  );
};

export default BillingContent;